// src/queries/bookingApiConfig.js

import { STALE_TIME, GC_TIME, QUERY_PRESETS } from "@/queries/queryTimeConfig";

/**
 * BOOKING API CONFIGURATION
 * GET endpoints use pms-api REST routes (via Next.js rewrite proxy).
 * POST/PUT/DELETE endpoints kept as legacy (old PMS) — will migrate later.
 */

export const bookingsConfig = {
  /* ────────────────── GET (pms-api) ─────────────────── */

  // List bookings with filters + pagination
  // Supports: status, search, guestId, roomTypeId, checkInFrom/To, checkOutFrom/To
  getBookings: {
    key: "getBookings",
    url: "/api/v1/bookings",
    method: "GET",
    options: {
      ...QUERY_PRESETS.LIST,
    },
  },

  // Today's check-in queue — paginated, lines attached
  getCheckInQueue: {
    key: "getCheckInQueue",
    url: "/api/v1/bookings/check-in-queue",
    method: "GET",
    options: { ...QUERY_PRESETS.REALTIME_POLLING },
  },

  // Check-in detail for GROUP slug page — snapshot rates + addons, no folio
  getCheckInDetail: {
    key: "getCheckInDetail",
    url: (params) => `/api/v1/bookings/number/${params.bookingNumber}/check-in-detail`,
    method: "GET",
    options: { ...QUERY_PRESETS.DETAIL },
  },

  // Checkout queue — all CHECKED_IN bookings, paginated, with checkoutStatus flag
  getCheckoutQueue: {
    key: "getCheckoutQueue",
    url: "/api/v1/bookings/checkout-queue",
    method: "GET",
    options: { ...QUERY_PRESETS.REALTIME_POLLING },
  },

  // Checkout detail for GROUP slug page — penalty + consumed addons per line
  getCheckoutDetail: {
    key: "getCheckoutDetail",
    url: (params) => `/api/v1/bookings/number/${params.bookingNumber}/checkout-detail`,
    method: "GET",
    options: { ...QUERY_PRESETS.DETAIL },
  },

  // Folio summary (alias of getFolio used on detail page for always-visible summary)
  getFolioSummary: {
    key: "getFolioSummary",
    url: (params) => `/api/v1/folio/bookings/${params.bookingId}`,
    method: "GET",
    options: { ...QUERY_PRESETS.DETAIL },
  },

  // Get single booking by booking number
  getBookingByNumber: {
    key: "getBookingByNumber",
    url: (params) => `/api/v1/bookings/number/${params.bookingNumber}`,
    method: "GET",
    options: {
      ...QUERY_PRESETS.DETAIL,
    },
  },

  // Get selected board plans + addon status timeline for booking
  getSelectedServicesStatus: {
    key: "getSelectedServicesStatus",
    url: (params) =>
      `/api/v1/bookings/number/${params.bookingNumber}/selected-services`,
    method: "GET",
    options: {
      ...QUERY_PRESETS.DETAIL,
    },
  },

  // Today's check-ins: use list endpoint with status=CONFIRMED + today's date
  getTodayCheckIns: {
    key: "getTodayCheckIns",
    url: "/api/v1/bookings",
    method: "GET",
    options: {
      ...QUERY_PRESETS.REALTIME_POLLING,
    },
  },

  // Today's check-outs: use list endpoint with status=CHECKED_IN + today's date
  getTodayCheckOuts: {
    key: "getTodayCheckOuts",
    url: "/api/v1/bookings",
    method: "GET",
    options: {
      ...QUERY_PRESETS.REALTIME_POLLING,
    },
  },

  // Check availability (inventory endpoint)
  checkAvailability: {
    key: "checkAvailability",
    url: "/api/v1/inventory/availability",
    method: "GET",
    options: {
      staleTime: STALE_TIME.REALTIME,
      gcTime: GC_TIME.NORMAL,
      refetchOnWindowFocus: true,
      refetchInterval: 15_000,
    },
  },

  // Get available rooms for a room type
  getAvailableRoomsForBooking: {
    key: "getAvailableRoomsForBooking",
    url: "/api/v1/rooms",
    method: "GET",
    options: {
      staleTime: STALE_TIME.REALTIME,
      gcTime: GC_TIME.NORMAL,
      refetchOnWindowFocus: false,
      enabled: false,
    },
  },

  // Max available rooms (inventory)
  getMaxAvailableRooms: {
    key: "getMaxAvailableRooms",
    url: "/api/v1/inventory/availability",
    method: "GET",
    options: {
      staleTime: STALE_TIME.REALTIME,
      gcTime: GC_TIME.NORMAL,
      refetchOnWindowFocus: false,
      enabled: false,
    },
  },

  // Calculate cancellation penalty preview
  calculateCancellation: {
    key: "calculateCancellation",
    url: (params) =>
      `/api/v1/bookings/number/${params.bookingNumber}/calculate-cancellation`,
    method: "GET",
    options: {
      ...QUERY_PRESETS.DETAIL,
    },
  },

  // Calculate early departure penalty preview
  calculateEarlyDeparture: {
    key: "calculateEarlyDeparture",
    url: (params) =>
      `/api/v1/bookings/number/${params.bookingNumber}/calculate-early-departure`,
    method: "GET",
    options: {
      ...QUERY_PRESETS.DETAIL,
    },
  },

  // Calculate checkout details — routes to per-line endpoint when lineNumber is provided
  calculateCheckout: {
    key: "calculateCheckout",
    url: (params) =>
      params.lineNumber
        ? `/api/v1/bookings/${params.bookingId}/lines/${params.lineNumber}/calculate-checkout`
        : `/api/v1/bookings/number/${params.bookingNumber}/calculate-checkout`,
    method: "GET",
    options: {
      ...QUERY_PRESETS.DETAIL,
    },
  },

  /* ────────────────── POST/PATCH (pms-api REST) ─────────────────── */

  createBooking: {
    key: "createBooking",
    url: "/api/v1/bookings",
    method: "POST",
    options: {
      retry: 2,
      invalidateKeys: [
        "getBookings",
        "getTodayCheckIns",
        "getTodayCheckOuts",
        "checkAvailability",
        "getCalendarData",
      ],
    },
  },

  checkIn: {
    key: "checkIn",
    url: (data) => `/api/v1/bookings/${data.id}/check-in`,
    method: "PATCH",
    options: {
      retry: 2,
      invalidateKeys: [
        "getBookings",
        "getBookingByNumber",
        "getSelectedServicesStatus",
        "getTodayCheckIns",
        "getCalendarData",
        "getAvailableRoomsForBooking",
        "getRooms",
      ],
    },
  },

  checkOut: {
    key: "checkOut",
    url: (data) => `/api/v1/bookings/${data.bookingId || data.id}/check-out`,
    method: "PATCH",
    options: {
      retry: 2,
      timeout: 300000,
      invalidateKeys: [
        "getBookings",
        "getBookingByNumber",
        "getSelectedServicesStatus",
        "getTodayCheckOuts",
        "getAvailableRoomsForBooking",
        "getCalendarData",
      ],
    },
  },

  cancelBooking: {
    key: "cancelBooking",
    url: (data) => `/api/v1/bookings/${data.id}/cancel`,
    method: "PATCH",
    options: {
      retry: 2,
      invalidateKeys: [
        "getBookings",
        "getBookingByNumber",
        "getSelectedServicesStatus",
        "getAvailableRoomsForBooking",
        "getCalendarData",
      ],
    },
  },

  cancelBookingLine: {
    key: "cancelBookingLine",
    url: (data) => `/api/v1/bookings/${data.id}/lines/${data.lineNumber}/cancel`,
    method: "PATCH",
    options: {
      retry: 0,
      invalidateKeys: [
        "getBookings",
        "getBookingByNumber",
        "getSelectedServicesStatus",
        "getCalendarData",
      ],
    },
  },

  checkoutLine: {
    key: "checkoutLine",
    url: (data) => `/api/v1/bookings/${data.id}/lines/${data.lineNumber}/checkout`,
    method: "PATCH",
    options: {
      retry: 0,
      invalidateKeys: [
        "getBookings",
        "getBookingByNumber",
        "getCheckoutQueue",
        "getCheckoutDetail",
        "getFolio",
        "getFolioSummary",
        "getPaymentTransactions",
        "getAllInvoices",
        "getCalendarData",
      ],
    },
  },

  changeRoom: {
    key: "changeRoom",
    url: (data) => `/api/v1/bookings/${data.bookingId}/change-room`,
    method: "PATCH",
    options: {
      retry: 2,
      invalidateKeys: [
        "getBookings",
        "getBookingByNumber",
        "getSelectedServicesStatus",
        "checkAvailability",
        "getAvailableRoomsForBooking",
        "getCalendarData",
        "getRooms",
      ],
    },
  },

  getBookingLineHistory: {
    key: "getBookingLineHistory",
    url: (params) => `/api/v1/bookings/${params.id}/lines/${params.lineNumber}/history`,
    method: "GET",
    options: { retry: 0 },
  },

  previewAmendLine: {
    key: "previewAmendLine",
    url: (data) => `/api/v1/bookings/${data.id}/lines/${data.lineNumber}/amend/preview`,
    method: "POST",
    options: { retry: 0 },
  },

  amendLine: {
    key: "amendLine",
    url: (data) => `/api/v1/bookings/${data.id}/lines/${data.lineNumber}/amend`,
    method: "PATCH",
    options: {
      invalidateKeys: [
        "getBookings",
        "getBookingByNumber",
        "getSelectedServicesStatus",
        "getCalendarData",
        "getAvailableRoomsForBooking",
      ],
    },
  },

  previewExtendStay: {
    key: "previewExtendStay",
    url: (data) =>
      `/api/v1/bookings/${data.id}/lines/${data.lineNumber}/extend-stay/preview`,
    method: "POST",
    options: { retry: 0 },
  },

  extendStay: {
    key: "extendStay",
    url: (data) =>
      `/api/v1/bookings/${data.id}/lines/${data.lineNumber}/extend-stay`,
    method: "PATCH",
    options: {
      invalidateKeys: ["getBookings", "getBookingByNumber", "getCalendarData"],
    },
  },

  /* ────────────────── INVENTORY (pms-api REST) ─────────────────── */

  initializePropertyInventory: {
    key: "initializePropertyInventory",
    url: "/api/v1/inventory/initialize",
    method: "POST",
    options: {
      retry: 1,
      timeout: 60000,
      invalidateKeys: [
        "getCalendarData",
        "checkAvailability",
        "getMaxAvailableRooms",
      ],
    },
  },

  recalculateInventory: {
    key: "recalculateInventory",
    url: (data) => `/api/v1/inventory/recalculate/${data.roomTypeId}`,
    method: "PATCH",
    options: {
      retry: 1,
      invalidateKeys: [
        "getCalendarData",
        "checkAvailability",
        "getMaxAvailableRooms",
      ],
    },
  },
};
